<template>
  <div class="cartItem" v-if="!placeholder && Object.keys(itemEnrichData).length && !itemUpdating && !itemDowngrading">
    <div class="cartItem__info">
      <div class="cartItem__img">
        <div v-if="itemEnrichData?.is_subscription || item.properties.subscription_id" class="cartItem__sub--badge"></div>
        <a :href="`/products/` + item.handle + '?variant=' + item.variant_id">
          <ImageField class="cartItem__image" :obj="{'url':item.image.url,'alt':item.image.alt,'width':1000,'height': 1000}" width-param="width" sizes="291px"/>      
        </a>
      </div>
      <div class="cartItem__details">
        <div v-if="itemDiscountMessage" class="cartItem__discount-msg">{{ itemDiscountMessage }}</div>
        <a v-if="itemEnrichData?.title" class="cartItem__title" :href="`/products/` + item.handle + '?variant=' + item.variant_id">
          {{ itemEnrichData?.title  }}
        </a>
        <a v-else class="cartItem__title" :href="`/products/` + item.handle + '?variant=' + item.variant_id">
          {{ item.product_title  }}
        </a>
        <div v-if="itemEnrichData?.subtitle" class="cartItem__subtitle">{{ itemEnrichData?.subtitle }}</div>
        <div class="cartItem__variantTitle">{{ variantTitle }}</div>
        <div class="cartItem__actions">
          <button type="button" :disabled="isGiftWithPurchase" aria-label="Decrease cart item quantity" class="cartItem__action" @click.prevent="updateQty(item.line, item.quantity - 1, false)">
            <svg class="icon icon--xs">
              <use href="#minus"></use>
            </svg>
          </button>
          <span :class="{'cartItem--processing': updating}" class="cartItem__qty" v-text="item.quantity"></span>
          <button type="button" :disabled="isGiftWithPurchase" aria-label="Increase cart item quantity" class="cartItem__action" @click.prevent="updateQty(item.line, item.quantity + 1, false)">
            <svg class="icon icon--xs">
              <use href="#plus"></use>
            </svg>
          </button>
        </div>
        <div v-if="shopLocation !== 'GB'">
          <div v-if="hasSubscription && !isGiftWithPurchase" class="cartItem__frequency">
            <div v-if="itemEnrichData?.is_subscription || item.selling_plan_allocation?.selling_plan">
              <select v-if="!itemUpdating && !itemDowngrading" :disabled="itemUpdating" class="cartItem__frequency-selector"
                v-model="itemEnrichData.selling_plan_id"
                @change="updateDeliveryFrequency($event)"
              >
                <optgroup label="Pay Full Price">
                  <option value="onetime">One-Time Only</option>
                </optgroup>
                <optgroup label="Subscribe &amp; Save">
                  <option
                    v-for="selling_plan in itemEnrichData?.variant_selling_plans" 
                    :value="selling_plan.id"
                  >
                    {{ selling_plan.name }}
                  </option>
                </optgroup>
              </select>
              <span v-if="itemDowngrading" class="cartItem__frequency-change active">Downgrading...</span>
              <span v-if="itemUpdating" class="cartItem__frequency-change active">Updating...</span>
            </div>
            <div v-if="!itemEnrichData?.is_subscription && hasSubscription && !itemDowngrading && !isPreOrderInDescription">
              <div @click="() => { itemUpdating = true; upgradeToSubscription() }" :class="['cartItem__frequency-change', {'active': itemUpdating}]">
                <span v-if="!itemUpdating">
                  Upgrade to Subscription &amp; <span class="cartItem__frequency--savings">Save</span>
                </span>
                <span v-else>Upgrading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="cartItem__price-wrapper">
        <div v-if="isDiscountedPrice" class="cartItem__price">
          <span class="cartItem__price-compare">{{ comparePrice }}</span>
          <span>{{ formatPrice(item.final_price) }}</span>
        </div>
        <div v-else>
          {{ formatPrice(item.final_price) }}
        </div>
        <div v-if="!isGiftWithPurchase && !itemUpdating && !itemDowngrading"
          @click.prevent="updateQty(item.line, 0, false)"
          class="cartItem__remove"
        >
          Remove
        </div>
      </div>
    </div>
  </div>
  <div v-else class="cartItem__placeholder">
    <div class="cartItem__placeholder-image placeholder"></div>
    <div class="cartItem__placeholder-wrap">
      <div class="cartItem__placeholder-title placeholder"></div>
      <div class="cartItem__placeholder-label placeholder"></div>

      <div class="cartItem__placeholder-actions placeholder"></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, toRefs, computed } from 'vue';
  import {useStore} from "vuex";
  import cartHelpers from '../api';
  import ImageField from '../../global/image.vue'

  const props = defineProps({
    cart: {
      type: Array,
      default: () => ([])
    },

    item: {
      type: Object,
      default: () => ({})
    },

    placeholder: {
      type: Boolean,
      default: false
    },
  })

  const { item } = toRefs(props)
  const { status } = cartHelpers;
  const store = useStore();

  const shopLocation = ref(window.shopLocation);
  const itemDowngrading = ref(false);
  const itemUpdating = ref(false);
  const multiPackOptions = ['boxes', 'tubs', 'bottles'];
  const cart = computed(() => store.state.cart)
  const enrichData = computed(() => store.state.extraData);
  const cartStatus = computed(() => store.state.status)
  const updateLine = computed(() => store.state.updateLine)

  const isPreOrderInDescription = computed(() => {
    let productDescription = item.value.product_description;

    if (typeof productDescription !== 'undefined' && productDescription != '') {
      productDescription = productDescription.toLowerCase();
      return (productDescription.indexOf('pre-order') > -1);
    } else {
      return false;
    }
  });

  const isMultiPack = computed(() => {
    return item.value.options_with_values.some(option => multiPackOptions.includes(option.name.toLowerCase()));
  })

  const updating = computed(() =>{
    return updatingLine.value
  })

  const updatingLine = computed(() => {
    const line = item.value.line
    return updateLine.value === line && cartStatus.value === status.progress;
  })

  const itemEnrichData = computed(() => {
    const key = item.value.key;
    return enrichData.value.find(variant => variant.key === key) || {}
  })

  const itemDiscountMessage = computed(() => {
    if (item.value.discounts.length > 0) {
      return item.value.discounts[0].title;
    }else{
      return false;
    }
  })

  const variantTitle = computed(() => {
    let title = item.value.variant_title;

    if (item.value.product_type === "Bundle" || item.value.product_type !== "Monthly Box") {
      return title;
    }

    // Replace numbers with "serving" or "servings" where appropriate
    title = title.replace(/\b(\d+)\b(?!\s*(Tub|Tubs|Bottle|Bottles|Box|Boxes))/g, match => {
      return match === "1" ? `${match} Serving` : `${match} Servings`;
    });

    // Remove " / One Time Purchase" or " | One Time Purchase"
    title = title.replace(/( \/|\|) One Time Purchase$/, '');

    return title;
  });

  const isGiftWithPurchase = computed(() => {
    return item.value.properties && item.value.properties._source && item.value.properties._source == 'Gift with Purchase'
  })

  const disabled = computed(() => {
    return itemEnrichData.value?.inventory_quantity <= item.value.quantity;
  })

  const hasSubscription = computed(() => {
    if(isSingleServing.value) return false;

    if(isMultiPack.value) return true;

    if (!itemEnrichData.value || !itemEnrichData.value.selling_plan_groups || !itemEnrichData.value.variant || !itemEnrichData.value.variant.selling_plan_allocations) {
      return false;
    }

    const groupIds = new Set(itemEnrichData.value.selling_plan_groups.map(group => group.id));
    return itemEnrichData.value.variant.selling_plan_allocations.some(allocation => groupIds.has(allocation.selling_plan_group_id));
  })

  const isSingleServing = computed(() => {
    return (item.value.variant_options[1] == '1');
  })

  const oneTimeVariant = computed(() => {
    return itemEnrichData.value?.product_variants.filter(variant => {
      if (item.value.variant_options.length === 3) {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] === item.value.variant_options[1] && variant.options[2] === 'One Time Purchase';
      } else {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] === 'One Time Purchase';
      }
    });
  });

  const firstSubVariant = computed(() => {
    return itemEnrichData.value?.product_variants.filter(variant => {
      if (item.value.variant_options.length === 3) {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] === item.value.variant_options[1] && variant.options[2] != 'One Time Purchase';
      } else {
        return variant.options[0] === item.value.variant_options[0] && variant.options[1] != 'One Time Purchase';
      }
    });
  });

  const isDiscountedPrice = computed(() => {
    const { properties } = item.value;
    const { is_subscription, compare_at_price } = itemEnrichData.value;

    return (
      (properties && properties.selected_plan) ||
      is_subscription ||
      item.value.final_price < item.value.price ||
      (compare_at_price && compare_at_price > item.value.price)
    );
  });

  const comparePrice = computed(() => {
    const { selling_plan_allocation, final_price, price } = item.value;
    const { compare_at_price, is_subscription } = itemEnrichData.value;

    if (isMultiPack.value) return formatPrice(compare_at_price);
    if (selling_plan_allocation?.selling_plan) return formatPrice(selling_plan_allocation.compare_at_price);
    if (is_subscription) return formatPrice(price);
    if (final_price < price) return formatPrice(price);
    if (compare_at_price && compare_at_price > price) return formatPrice(compare_at_price);

    return null; // Explicit return for clarity
  });

  const formatPrice = (price) => {
    return window.formattedPrice(price/100)
  }

  async function updateQty(line, quantity = 0, option = true) {
    if(quantity && item.value.quantity < itemEnrichData.value?.inventory_quantity || !option){
      await changeItem(line, quantity)
    }
  }

  const upgradeToSubscription = async () => {
    try {
      const isMultiPackVariant = isMultiPack.value;
      const itemId = isMultiPackVariant
        ? firstSubVariant.value[0].id
        : item.value.id;

      const sellingPlanId = isMultiPackVariant
      ? firstSubVariant.value[0]?.selling_plan_allocations.find(
          (allocation) =>
            allocation.selling_plan_group_id ===
            itemEnrichData.value.selling_plan_groups[0].id
        )?.selling_plan_id
      : itemEnrichData.value.selling_plan_groups[0].selling_plans[0].id;

      if (
        isMultiPackVariant ||
        (itemEnrichData.value.variant.selling_plan_allocations &&
          itemEnrichData.value.variant.selling_plan_allocations.length > 0)
      ) {
        await changeItem(item.value.line, 0);
        await addItem(itemId, item.value.line, item.value.properties, item.value.quantity, sellingPlanId);
      }
    } catch (error) {
      console.error("Error upgrading to subscription:", error);
    } finally {
      itemUpdating.value = false;
    }
  };

  const updateDeliveryFrequency = async (event) => {
    const isOneTime = event.target.value === 'onetime';
    const updateAction = isOneTime ? downgradeToOneTime : updateProperties;

    try {
      itemDowngrading.value = isOneTime;
      itemUpdating.value = !isOneTime;
      await updateAction(event.target.value);
    } finally {
      itemDowngrading.value = false;
      itemUpdating.value = false;
    }
  };

  const downgradeToOneTime = async () => {
    try {
      await changeItem(item.value.line, 0);
      const variantId = isMultiPack.value ? oneTimeVariant.value[0].id : item.value.id;
      await addItem(variantId,item.value.line, item.value.properties, item.value.quantity, null);
    } catch (error) {
      console.error("Error downgrading to one-time:", error);
    }
  };

  const updateProperties = async (value) => {
    try {
      await changeItem(item.value.line, item.value.quantity, Number(value));
      await store.dispatch("init");
    } catch (error) {
      console.error("Error updating properties:", error);
    }
  };

  const addItem = async (id, line, properties, quantity, sellingPlan) => {
    const itemAddedToCart = cart.value.items?.find(item =>
      item.variant_id === Number(id) &&
      (!properties || JSON.stringify(item.properties) === JSON.stringify(properties)) &&
      ((!sellingPlan && !item.selling_plan_allocation) ||
        (sellingPlan && item.selling_plan_allocation?.selling_plan.id === Number(sellingPlan)))
    );

    await store.dispatch("add", {
      id,
      quantity,
      ...(sellingPlan && { selling_plan: sellingPlan }),
    });

    // if (!itemAddedToCart) {

    // } else {
    //   await changeItem(line, itemAddedToCart.quantity + quantity, sellingPlan);
    //   await store.dispatch("init");
    // }
  };

  const changeItem = async (line, quantity, sellingPlan) => {
    return store.dispatch("change", {
      line: line,
      quantity: quantity,
      ...(sellingPlan && { selling_plan: sellingPlan }),
    });
  };
</script>